import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaBuilding, FaEye, FaCalendarAlt, FaUserFriends, FaInbox, FaCheckCircle, FaTimesCircle, FaSpinner, FaSync, FaEnvelope, FaHome } from 'react-icons/fa';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { getAllBookingEnquiries, updateEnquiryStatus } from '../../Functions/FetchEnquiries';

const StatCard = styled(motion.div)`
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.05'});
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  transition: all 0.3s ease;
  height: 100%;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0,0,0,${props => props.darkMode ? '0.3' : '0.1'});
  }
  @media (max-width: 576px) {
    padding: 12px 10px;
  }
`;

const StatValue = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin: 10px 0;
  color: ${props => props.color || (props.darkMode ? '#f5f5f5' : '#2c3e50')};
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 8px 0;
  }
  
  @media (max-width: 576px) {
    font-size: 1.2rem;
    margin: 4px 0;
  }
`;

const StatTitle = styled.p`
  font-size: 1rem;
  color: ${props => props.darkMode ? '#b0b0b0' : '#7f8c8d'};
  margin: 0;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const IconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || (props.darkMode ? '#1a365d' : '#e2f5fe')};
  color: ${props => props.color || '#3498db'};
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
  
  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
    min-width: 32px;
  }
`;

const CardContainer = styled.div`
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    .col-md-6, .col-xl-3 {
      margin-bottom: 15px;
    }
  }
`;

const ChartContainer = styled(motion.div)`
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.05'});
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  height: auto;
  min-height: 300px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    min-height: 250px;
  }
`;

const ResponsiveStatsContainer = styled.div`
  @media (max-width: 768px) {
    .stat-value {
      font-size: 1.5rem;
    }
    
    .stat-icon {
      width: 40px;
      height: 40px;
      font-size: 1.25rem;
    }
  }
  
  @media (max-width: 576px) {
    .text-muted {
      font-size: 0.7rem;
      white-space: normal;
      display: block;
      margin-top: 3px;
      line-height: 1.2;
    }
  }
`;

// Update these styled components to handle different enquiry types
const EnquiryCard = styled(motion.div)`
  background: ${props => props.darkMode ? '#2d2d2d' : '#ffffff'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, ${props => props.darkMode ? '0.25' : '0.1'});
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => {
    // Different colors based on enquiry type and status
    if (props.type === 'contact') {
      return props.darkMode ? '#9b59b6' : '#d6a1f9'; // Purple for contact enquiries
    }
    
    switch (props.status) {
      case 'new': return props.darkMode ? '#3498db' : '#bde0ff';
      case 'responded': return props.darkMode ? '#f39c12' : '#ffeaa7';
      case 'completed': return props.darkMode ? '#2ecc71' : '#d4f9e2';
      case 'canceled': return props.darkMode ? '#e74c3c' : '#ffcdd2';
      default: return props.darkMode ? '#95a5a6' : '#e0e0e0';
    }
  }};
  
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    height: 40px;
    background-color: ${props => props.type === 'contact' 
      ? (props.darkMode ? '#6c3483' : '#e9d5ff') // Purple badge for contact
      : 'transparent'};
    clip-path: polygon(0 0, 100% 0, 100% 100%);
    opacity: 0.7;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'new': return '#e3f2fd';
      case 'responded': return '#fff3e0';
      case 'completed': return '#e8f5e9';
      case 'canceled': return '#ffebee';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'new': return '#1976d2';
      case 'responded': return '#e65100';
      case 'completed': return '#2e7d32';
      case 'canceled': return '#c62828';
      default: return '#757575';
    }
  }};
`;

// Update EnquiryHeader styling for better alignment
const EnquiryHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
`;

// Update EnquiryDetail styling for better mobile alignment
const EnquiryDetail = styled.div`
  display: flex;
  margin-bottom: 8px;
  flex-wrap: wrap;
  text-align: left;
  
  @media (max-width: 576px) {
    flex-direction: column;
    margin-bottom: 10px;
    align-items: flex-start;
    width: 100%;
  }
`;

// Fix DetailValue to maintain left alignment on mobile
const DetailValue = styled.span`
  margin-right: 15px;
  text-align: left;
  color: ${props => props.darkMode ? '#f5f5f5' : '#333'};
  
  @media (max-width: 576px) {
    margin-bottom: 5px;
    display: inline-block;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  margin-right: 6px;
  color: ${props => props.darkMode ? '#aaa' : '#666'};
`;

const DetailSeparator = styled.span`
  margin: 0 6px;
  color: ${props => props.darkMode ? '#555' : '#ddd'};
  
  @media (max-width: 576px) {
    display: none;
  }
`;

// Also update EnquiryActions for better mobile layout
const EnquiryActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
  }
`;

const EnquiryType = styled.span`
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 8px;
  vertical-align: middle;
  
  @media (max-width: 576px) {
    margin-left: 0;
    margin-top: 4px;
    align-self: flex-start;
  }
  
  background-color: ${props => {
    if (props.type === 'contact') {
      return props.darkMode ? '#4a235a' : '#f5e6ff';
    }
    return props.darkMode ? '#1a5276' : '#e6f7ff';
  }};
  
  color: ${props => {
    if (props.type === 'contact') {
      return props.darkMode ? '#d6a1f9' : '#9b59b6';
    }
    return props.darkMode ? '#85c1e9' : '#0077cc';
  }};
`;

const StatisticsPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-grow: 1;
  padding: 20px;
  background: ${props => props.darkMode ? '#252525' : '#f9f9f9'};
  border-radius: 8px;
  margin-top: 15px;
`;

const PropertiesListContainer = styled.div`
  margin-top: 15px;
  overflow-y: auto;
  max-height: 230px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.darkMode ? '#252525' : '#f5f5f5'};
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.darkMode ? '#555' : '#ddd'};
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.darkMode ? '#777' : '#bbb'};
  }
`;

const PropertyListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid ${props => props.darkMode ? '#444' : '#eee'};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.darkMode ? '#333' : '#f5f5f5'};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const PropertyInfo = styled.div`
  max-width: 70%;
  overflow: hidden;
  text-align: left;
`;

const PropertyTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const PropertyLocation = styled.div`
  font-size: 0.8rem;
  color: ${props => props.darkMode ? '#aaa' : '#777'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  width: 100%;
`;

const ViewBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.darkMode ? '#1a5276' : '#e3f2fd'};
  color: ${props => props.darkMode ? '#85c1e9' : '#0d47a1'};
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  
  svg {
    margin-right: 4px;
  }
`;

const Dashboard = ({
  properties = [],
  darkMode,
  siteStats = { totalVisits: 0 },
  bookingEnquiries = [], // Using this prop
  loadingEnquiries = false, // Using this prop
  refreshEnquiries, // Using this prop
  setActivePage // This prop for navigation
}) => {
  // Calculate some stats
  const propertyCount = properties.length;
  const avgPrice = properties.length
    ? Math.round(properties.reduce((acc, prop) => acc + (prop.startPrice || 0), 0) / properties.length)
    : 0;

  // Get total views from all properties with better handling of undefined viewCount
  const totalViews = properties.reduce((total, property) => {
    // Log property and its viewCount for debugging
    console.log(`Property ${property.propertyTitle} has ${property.viewCount || 0} views`);
    return total + (parseInt(property.viewCount) || 0);
  }, 0);

  // Get total site visits
  const totalVisits = siteStats.totalVisits || 0;

  // Log the calculated stats for debugging
  useEffect(() => {
    console.log('Dashboard stats:', {
      propertyCount,
      avgPrice,
      totalViews,
      totalVisits,
      properties: properties.map(p => ({ title: p.propertyTitle, views: p.viewCount || 0 }))
    });
  }, [propertyCount, avgPrice, totalViews, totalVisits, properties]);

  // Format the date of last visit if available
  const lastVisitDate = siteStats.lastVisit
    ? new Date(siteStats.lastVisit).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    : 'Never';

  // State for booking enquiries - use the props directly
  const [enquiries, setEnquiries] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Update to use the refreshEnquiries prop if available
  useEffect(() => {
    // If we have direct enquiries from props, use them
    if (bookingEnquiries && bookingEnquiries.length > 0) {
      setEnquiries(bookingEnquiries);
    } else {
      // Otherwise fetch them directly
      const fetchEnquiries = async () => {
        try {
          // We don't have access to setLoadingEnquiries here
          const data = await getAllBookingEnquiries();
          setEnquiries(data);
        } catch (error) {
          console.error("Error fetching enquiries:", error);
          toast.error("Failed to load booking enquiries");
        }
      };
      fetchEnquiries();
    }
  }, [bookingEnquiries, refreshTrigger]);

  // Handle status change for an enquiry
  const handleStatusChange = async (enquiry, newStatus) => {
    try {
      await updateEnquiryStatus(
        enquiry.year,
        enquiry.month,
        enquiry.date,
        enquiry.id,
        newStatus
      );

      // Show success notification
      toast.success(`Enquiry status updated to ${newStatus}`);

      // If refreshEnquiries prop is provided, use it, otherwise use local refresh
      if (refreshEnquiries) {
        refreshEnquiries();
      } else {
        setRefreshTrigger(prev => prev + 1);
      }

    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update enquiry status");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status label with icon
  const getStatusLabel = (status) => {
    switch (status) {
      case 'new':
        return <><FaInbox className="me-1" /> New</>;
      case 'responded':
        return <><FaSpinner className="me-1" /> Responded</>;
      case 'completed':
        return <><FaCheckCircle className="me-1" /> Completed</>;
      case 'canceled':
        return <><FaTimesCircle className="me-1" /> Canceled</>;
      default:
        return <>{status}</>;
    }
  };

  return (
    <div className="container-fluid px-4">
      <motion.h1
        className="mt-4 mb-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Dashboard
      </motion.h1>

      <ResponsiveStatsContainer>
        <CardContainer className="row">
          {/* Property Count Card */}
          <div className="col-xl-3 col-md-6 col-sm-6 col-6">
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              darkMode={darkMode}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <StatTitle darkMode={darkMode}>Total Properties</StatTitle>
                  <StatValue className="stat-value" color="#3498db" darkMode={darkMode}>{propertyCount}</StatValue>
                </div>
                <IconContainer className="stat-icon" bg={darkMode ? '#103a52' : '#e2f5fe'} color="#3498db" darkMode={darkMode}>
                  <FaBuilding />
                </IconContainer>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: window.innerWidth <= 576 ? '0.75rem' : 'inherit' }}>Active listings on the platform</p>
            </StatCard>
          </div>

          {/* Average Price Card */}
          <div className="col-xl-3 col-md-6 col-sm-6 col-6">
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              darkMode={darkMode}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <StatTitle darkMode={darkMode}>Average Price</StatTitle>
                  <StatValue className="stat-value" color="#2ecc71" darkMode={darkMode}>₹{avgPrice.toLocaleString()}</StatValue>
                </div>
                <IconContainer className="stat-icon" bg={darkMode ? '#123d29' : '#e2fef5'} color="#2ecc71" darkMode={darkMode}>
                  <FaCalendarAlt />
                </IconContainer>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: window.innerWidth <= 576 ? '0.75rem' : 'inherit' }}>Start Price across all properties</p>
            </StatCard>
          </div>

          {/* Property Views Card */}
          <div className="col-xl-3 col-md-6 col-sm-6 col-6">
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              darkMode={darkMode}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <StatTitle darkMode={darkMode}>Property Views</StatTitle>
                  <StatValue className="stat-value" color="#9b59b6" darkMode={darkMode}>{totalViews.toLocaleString()}</StatValue>
                </div>
                <IconContainer className="stat-icon" bg={darkMode ? '#2a1758' : '#f5e2fe'} color="#9b59b6" darkMode={darkMode}>
                  <FaEye />
                </IconContainer>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: window.innerWidth <= 576 ? '0.75rem' : 'inherit' }}>Total property page views</p>
            </StatCard>
          </div>

          {/* Site Visits Card */}
          <div className="col-xl-3 col-md-6 col-sm-6 col-6">
            <StatCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              darkMode={darkMode}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <StatTitle darkMode={darkMode}>Site Visits</StatTitle>
                  <StatValue className="stat-value" color="#e74c3c" darkMode={darkMode}>{totalVisits.toLocaleString()}</StatValue>
                </div>
                <IconContainer className="stat-icon" bg={darkMode ? '#3a1b1a' : '#fee2e2'} color="#e74c3c" darkMode={darkMode}>
                  <FaUserFriends />
                </IconContainer>
              </div>
              <p className="text-muted mb-0" style={{ fontSize: window.innerWidth <= 576 ? '0.75rem' : 'inherit' }}>Last visited: {lastVisitDate}</p>
            </StatCard>
          </div>
        </CardContainer>
      </ResponsiveStatsContainer>

      <div className="row">
        <div className="col-xl-8">
          <ChartContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            darkMode={darkMode}
          >
            <h5>Property Views Statistics</h5>
            <StatisticsPlaceholder darkMode={darkMode}>
              <FaEye size={24} color={darkMode ? '#3498db' : '#0d47a1'} className="mb-3" />
              <h6>Total Property Views: {totalViews.toLocaleString()}</h6>
              <p className="text-muted mb-0">
                {propertyCount > 0 
                  ? `Average ${Math.round(totalViews / propertyCount)} views per property`
                  : 'Add properties to start tracking views'}
              </p>
            </StatisticsPlaceholder>
          </ChartContainer>
        </div>

        <div className="col-xl-4">
          <ChartContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            darkMode={darkMode}
          >
            <h5>Most Viewed Properties</h5>
            {properties.length > 0 ? (
              <PropertiesListContainer darkMode={darkMode}>
                {properties
                  .filter(prop => (prop.viewCount || 0) > 0)
                  .sort((a, b) => (parseInt(b.viewCount) || 0) - (parseInt(a.viewCount) || 0))
                  .slice(0, 5)
                  .map((prop, index) => (
                    <PropertyListItem key={index} darkMode={darkMode}>
                      <PropertyInfo>
                        <PropertyTitle darkMode={darkMode}>{prop.propertyTitle}</PropertyTitle>
                        <PropertyLocation darkMode={darkMode}>{prop.location}</PropertyLocation>
                      </PropertyInfo>
                      <ViewBadge darkMode={darkMode}>
                        <FaEye size={12} /> {prop.viewCount || 0}
                      </ViewBadge>
                    </PropertyListItem>
                  ))}
                {properties.filter(prop => (prop.viewCount || 0) > 0).length === 0 && (
                  <div className="text-center p-4">
                    <p className="text-muted mb-0">No property views recorded yet</p>
                  </div>
                )}
              </PropertiesListContainer>
            ) : (
              <StatisticsPlaceholder darkMode={darkMode}>
                <FaBuilding size={24} color={darkMode ? '#3498db' : '#0d47a1'} className="mb-3" />
                <p className="text-muted mb-0">No properties available</p>
              </StatisticsPlaceholder>
            )}
          </ChartContainer>
        </div>
      </div>

      {/* Booking Enquiries Section */}
      <div className="row mt-4">
        <div className="col-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Recent Booking Enquiries</h4>
              <div>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    // Use refreshEnquiries prop if available, otherwise use local refresh
                    if (refreshEnquiries) {
                      refreshEnquiries();
                    } else {
                      setRefreshTrigger(prev => prev + 1);
                    }
                  }}
                  disabled={loadingEnquiries}
                >
                  <FaSync className={`me-1 ${loadingEnquiries ? 'fa-spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>

            {loadingEnquiries ? (
              <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading enquiries...</p>
              </div>
            ) : enquiries.length === 0 ? (
              <div className="alert alert-info">
                No booking enquiries found.
              </div>
            ) : (
              <div className="enquiries-list">
                {enquiries.slice(0, 5).map((enquiry) => (
                  <EnquiryCard
                    key={enquiry.id}
                    darkMode={darkMode}
                    status={enquiry.status}
                    type={enquiry.type} // Pass the enquiry type to the component
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <EnquiryHeader>
                      <div className="d-flex align-items-center flex-wrap">
                        <h5 className="mb-0 me-2">{enquiry.name}</h5>
                        <EnquiryType 
                          type={enquiry.type === 'contact' ? 'contact' : 'booking'} 
                          darkMode={darkMode}
                        >
                          {enquiry.type === 'contact' 
                            ? <><FaEnvelope size={12} /> Contact</> 
                            : <><FaHome size={12} /> Booking</>}
                        </EnquiryType>
                      </div>
                      <StatusBadge status={enquiry.status}>
                        {getStatusLabel(enquiry.status)}
                      </StatusBadge>
                    </EnquiryHeader>

                    {/* Show different details based on enquiry type */}
                    {enquiry.type === 'contact' ? (
                      // Contact form enquiry
                      <>
                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Contact:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{enquiry.email} | {enquiry.phone}</DetailValue>
                        </EnquiryDetail>

                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Received:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{formatDate(enquiry.submittedAt || enquiry.enquiryDate)}</DetailValue>
                        </EnquiryDetail>
                        
                        {/* Show message preview for contact enquiries */}
                        {enquiry.message && (
                          <EnquiryDetail>
                            <div className={`p-2 mt-1 rounded w-100 ${darkMode ? 'bg-dark text-light-50' : 'bg-light text-muted'}`} style={{
                              fontSize: '0.9rem',
                              fontStyle: 'italic',
                              borderLeft: `3px solid ${darkMode ? '#6c3483' : '#e9d5ff'}`,
                              maxHeight: '60px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'left'
                            }}>
                              "{enquiry.message.length > 100 ? enquiry.message.substring(0, 100) + '...' : enquiry.message}"
                            </div>
                          </EnquiryDetail>
                        )}
                      </>
                    ) : (
                      // Property booking enquiry - original structure
                      <>
                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Property:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{enquiry.propertyName}</DetailValue>
                          <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                          <DetailLabel darkMode={darkMode}>Location:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{enquiry.location}</DetailValue>
                        </EnquiryDetail>

                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Dates:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{formatDate(enquiry.checkInDate)} - {formatDate(enquiry.checkOutDate)}</DetailValue>
                          <DetailSeparator darkMode={darkMode}>•</DetailSeparator>
                          <DetailLabel darkMode={darkMode}>Guests:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{enquiry.adults} Adults, {enquiry.children || 0} Children</DetailValue>
                        </EnquiryDetail>

                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Contact:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{enquiry.email} | {enquiry.phone}</DetailValue>
                        </EnquiryDetail>

                        <EnquiryDetail>
                          <DetailLabel darkMode={darkMode}>Received:</DetailLabel>
                          <DetailValue darkMode={darkMode}>{formatDate(enquiry.enquiryDate)}</DetailValue>
                        </EnquiryDetail>
                      </>
                    )}

                    <EnquiryActions>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleStatusChange(enquiry, 'responded')}
                        disabled={enquiry.status === 'responded'}
                      >
                        Mark Responded
                      </button>
                      <button
                        className="btn btn-sm btn-outline-success me-2"
                        onClick={() => handleStatusChange(enquiry, 'completed')}
                        disabled={enquiry.status === 'completed'}
                      >
                        Mark Completed
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleStatusChange(enquiry, 'canceled')}
                        disabled={enquiry.status === 'canceled'}
                      >
                        Cancel
                      </button>
                    </EnquiryActions>
                  </EnquiryCard>
                ))}

                {enquiries.length > 0 && enquiries.length > 5 && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => {
                        console.log("Navigating to enquiries tab");
                        if (typeof setActivePage === 'function') {
                          setActivePage('enquiries');
                        } else {
                          console.error("setActivePage is not a function", setActivePage);
                        }
                      }}
                    >
                      View All Enquiries ({enquiries.length})
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
